export const USER_ROLES = ['User', 'ProjectManager', 'Administrator'] as const;

export type UserRole = (typeof USER_ROLES)[number];

export const DEFAULT_USER_ROLE: UserRole = 'User';

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  User: 'Användare',
  ProjectManager: 'Projektledare',
  Administrator: 'Administratör',
};

export function getUserRoleLabel(role: UserRole): string {
  return USER_ROLE_LABELS[role];
}

export function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && USER_ROLES.includes(value as UserRole);
}

const NUMERIC_ROLE_MAP: Record<number, UserRole> = {
  0: 'User',
  1: 'ProjectManager',
  2: 'Administrator',
};

export function normalizeUserRole(value: unknown): UserRole {
  if (isUserRole(value)) {
    return value;
  }

  if (typeof value === 'number' && value in NUMERIC_ROLE_MAP) {
    return NUMERIC_ROLE_MAP[value];
  }

  return DEFAULT_USER_ROLE;
}

const ROLE_PRIORITY: Record<UserRole, number> = {
  User: 0,
  ProjectManager: 1,
  Administrator: 2,
};

export function hasRequiredRole(
  role: UserRole,
  required?: UserRole,
): boolean {
  if (!required) {
    return true;
  }

  return ROLE_PRIORITY[role] >= ROLE_PRIORITY[required];
}

export function combineRoleRequirement(
  ...roles: Array<UserRole | undefined>
): UserRole | undefined {
  let result: UserRole | undefined;

  for (const role of roles) {
    if (!role) {
      continue;
    }

    if (!result || ROLE_PRIORITY[role] > ROLE_PRIORITY[result]) {
      result = role;
    }
  }

  return result;
}
